import createClient from "openapi-fetch";

import { session } from "@/auth/session";

import { ApiError } from "./errors";
import type { paths } from "./schema";
import type { Problem, Session } from "./types";

// An absolute base, so requests are built the same way in every runtime.
export const baseUrl: string = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const AUTH_PATHS = ["/v1/auth/login", "/v1/auth/refresh", "/v1/auth/logout"];
const isAuthPath = (url: string) => AUTH_PATHS.some((path) => new URL(url, location.origin).pathname === path);

let refreshing: Promise<boolean> | null = null;

/**
 * Exchange the refresh token for a new pair. Concurrent callers share one request
 * (single flight): ten queries failing with 401 at once cause one refresh, not ten.
 * Resolves false when the session is over.
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    const refreshToken = session.getRefreshToken();
    if (!refreshToken) {
      session.clear();
      return false;
    }
    try {
      const response = await fetch(`${baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (response.ok) {
        session.set((await response.json()) as Session);
        return true;
      }
      // 401/403: the session is really over. Anything else (5xx) may be temporary: keep the token.
      if (response.status === 401 || response.status === 403) session.clear();
      return false;
    } catch {
      return false; // offline: keep the token and let the caller surface the error
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

function withToken(request: Request): Request {
  const token = session.getAccessToken();
  if (!token) return request;
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return new Request(request, { headers });
}

/** fetch that attaches the access token and, on a 401, refreshes once and retries. */
async function authFetch(request: Request): Promise<Response> {
  if (isAuthPath(request.url)) return fetch(request);
  const retry = request.clone();
  const response = await fetch(withToken(request));
  if (response.status !== 401) return response;
  return (await refreshSession()) ? fetch(withToken(retry)) : response;
}

export const api = createClient<paths>({ baseUrl, fetch: authFetch });

type Result<T> = { data?: T; error?: unknown; response: Response };

/**
 * Turn an openapi-fetch result into a value, or throw an ApiError.
 * Query and mutation functions use it so that React Query sees ordinary rejections.
 */
export async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await call;
  } catch {
    throw ApiError.network();
  }
  if (!result.response.ok) {
    throw ApiError.fromProblem(result.response.status, result.error as Partial<Problem> | undefined);
  }
  return result.data as T;
}
